import Icon from "components/icon";
import Typography from "components/typography";
import React, { useState, useMemo } from "react";
import ReactDOM from "react-dom";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { FieldError } from "react-hook-form";
import { useTranslation } from "react-i18next";


const toDateStr = (y: number, m: number, d: number) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const formatDisplay = (dateStr: string) => {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}/${y}`;
};

const getDaysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();

const getFirstDayOffset = (y: number, m: number) => {
  const day = new Date(y, m, 1).getDay();
  return day === 0 ? 6 : day - 1;
};

type DateRangePickerProps = {
  fromValue?: string;
  toValue?: string;
  onChange?: (from: string, to: string) => void;
  label?: string;
  error?: FieldError;
  helperText?: string;
  reserveHelperSpace?: boolean;
  disabled?: boolean;
  parentClassName?: string;
  placeholder?: string;
  "data-testid"?: string;
};

const DateRangePicker = ({
  fromValue = "",
  toValue = "",
  onChange,
  label,
  error,
  helperText,
  reserveHelperSpace = true,
  disabled = false,
  parentClassName = "",
  placeholder = "Select date range",
  "data-testid": dataTestId,
}: DateRangePickerProps) => {
  const today = new Date();
  const todayStr = toDateStr(today.getFullYear(), today.getMonth(), today.getDate());

  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"days" | "years">("days");
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [hoverDate, setHoverDate] = useState<string | null>(null);

  const { t } = useTranslation();

  const MONTHS = useMemo(() => [
      t("month-january",{ defaultValue: "January"}),
      t("month-february",{ defaultValue: "February"}),
      t("month-march",{ defaultValue: "March"}),
      t("month-april",{ defaultValue: "April"}),
      t("month-may",{ defaultValue: "May"}),
      t("month-june",{ defaultValue: "June"}),
      t("month-july",{ defaultValue: "July"}),
      t("month-august",{ defaultValue: "August"}),
      t("month-september",{ defaultValue: "September"}),
      t("month-october",{ defaultValue: "October"}),
      t("month-november",{ defaultValue: "November"}),
      t("month-december",{ defaultValue: "December"}),
    ], [t])
  
    const DAY_LABELS = useMemo(() => [
      t("day-sh-mon", { defaultValue: "Mon" }),
      t("day-sh-tue", { defaultValue: "Tue" }),
      t("day-sh-wed", { defaultValue: "Wed" }),
      t("day-sh-thu", { defaultValue: "Thu" }),
      t("day-sh-fri", { defaultValue: "Fri" }),
      t("day-sh-sat", { defaultValue: "Sat" }),
      t("day-sh-sun", { defaultValue: "Sun" }),
    ], [t])

  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open: isOpen,
    onOpenChange: (nextOpen) => {
      if (disabled) return;
      if (!nextOpen) {
        setHoverDate(null);
      }
      setIsOpen(nextOpen);
    },
    placement: "bottom-start",
    strategy: "fixed",
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  const handleOpen = () => {
    if (disabled) return;
    if (!isOpen && fromValue) {
      setViewYear(Number(fromValue.split("-")[0]));
      setViewMonth(Number(fromValue.split("-")[1]) - 1);
    }
    setViewMode("days");
    setIsOpen((prev) => !prev);
  };

  const handleDayClick = (dateStr: string) => {
    if (!fromValue || toValue) {
      // Start a fresh selection
      onChange?.(dateStr, "");
    } else if (dateStr >= fromValue) {
      onChange?.(fromValue, dateStr);
      setHoverDate(null);
    } else {
      // Clicked before start — restart
      onChange?.(dateStr, "");
    }
  };

  const navigateMonth = (delta: number) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const navigateYearRange = (delta: number) => {
    setViewYear((prev) => prev + delta * 12);
  };

  // Range rendering helpers
  const effectiveTo = toValue || hoverDate || "";
  const rangeStart = fromValue && effectiveTo
    ? fromValue <= effectiveTo ? fromValue : effectiveTo
    : fromValue;
  const rangeEnd = fromValue && effectiveTo
    ? fromValue <= effectiveTo ? effectiveTo : fromValue
    : "";
  const isSingleDay = !!rangeStart && rangeStart === rangeEnd;

  const displayText = () => {
    if (!fromValue) return "";
    if (!toValue) return `${formatDisplay(fromValue)} – ...`;
    return `${formatDisplay(fromValue)} – ${formatDisplay(toValue)}`;
  };

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDayOffset = getFirstDayOffset(viewYear, viewMonth);

  const startYear = viewYear - (viewYear % 12);
  const years = Array.from({ length: 12 }, (_, i) => startYear + i);

  const triggerClasses = [
    "flex items-center gap-2 rounded border bg-white px-3 py-2 transition",
    error ? "border-error shadow-[0_0_0_3px] shadow-error-500/15" : "border-border",
    isOpen && !error ? "border-primary-600 ring-primary-200 ring-offset-1 ring-2" : "",
    isOpen && error ? "ring-error" : "",
    disabled ? "bg-whiteScale-80 border-whiteScale-60 cursor-not-allowed" : "cursor-pointer",
  ].filter(Boolean).join(" ");

  const dropdownClasses = [
    "fixed z-dropdown bg-white border border-border rounded-xl shadow-[0_8px_32px_rgba(15,23,42,0.12)] p-3 w-72",
  ].join(" ");

  const helperTone = error ? "error" : "muted";
  const helperMessage = error?.message ?? helperText;

  return (
    <div className={`w-full flex flex-col gap-[7px] relative ${parentClassName}`}>
      {label && (
        <label className="flex items-center gap-1 text-sm text-text-primary">
          {label}
        </label>
      )}

      {/* Trigger */}
      <div className={triggerClasses} onClick={handleOpen} data-testid={dataTestId} ref={refs.setReference} {...getReferenceProps()}>
        <Icon name="calendar" size={16} className="text-tertiary shrink-0" />
        <span className={`flex-1 text-sm truncate ${displayText() ? "text-text-primary" : "text-tertiary"}`}>
          {displayText() || placeholder}
        </span>
        {(fromValue || toValue) && !disabled && (
          <button
            type="button"
            className="text-tertiary hover:text-text-primary shrink-0 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onChange?.("", "");
            }}
          >
            <Icon name="close-circle" size={16} />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen &&
        ReactDOM.createPortal(
          <div
            className={dropdownClasses}
            style={{
              ...floatingStyles,
              visibility: isPositioned ? "visible" : "hidden",
            }}
            ref={refs.setFloating}
            {...getFloatingProps()}
          >

          {/* Selected range pills */}
          <div className="flex items-center gap-2 mb-3">
            <div className={`flex-1 rounded-lg border px-2 py-1 text-center text-xs transition-colors ${fromValue ? "border-primary-300 bg-primary-50 text-primary-700 font-medium" : "border-border text-tertiary"}`}>
              {fromValue ? formatDisplay(fromValue) : "Start"}
            </div>
            <Icon name="arrow-right-2" size={12} className="text-tertiary shrink-0" />
            <div className={`flex-1 rounded-lg border px-2 py-1 text-center text-xs transition-colors ${toValue ? "border-primary-300 bg-primary-50 text-primary-700 font-medium" : "border-border text-tertiary"}`}>
              {toValue ? formatDisplay(toValue) : "End"}
            </div>
          </div>

          {/* Nav bar */}
          <div className="flex items-center justify-between mb-4 border-b border-greyScale-90 pb-2">
            <button
              type="button"
              className="p-1 rounded-full hover:bg-greyScale-90 text-text-primary transition-colors"
              onClick={() => viewMode === "days" ? navigateMonth(-1) : navigateYearRange(-1)}
            >
              <Icon name="arrow-left-2" size={18} />
            </button>

            <button
              type="button"
              className="flex items-center gap-1.5 px-3 py-1 rounded-lg hover:bg-greyScale-90 transition-colors group"
              onClick={() => setViewMode(viewMode === "days" ? "years" : "days")}
            >
              <span className="text-sm font-bold text-text-primary group-hover:text-primary-600">
                {viewMode === "days"
                  ? `${MONTHS[viewMonth]} ${viewYear}`
                  : `${years[0]} – ${years[years.length - 1]}`}
              </span>
              <Icon
                name={viewMode === "days" ? "chevron-down" : "chevron-up"}
                size={14}
                className="text-tertiary group-hover:text-primary-600"
              />
            </button>

            <button
              type="button"
              className="p-1 rounded-full hover:bg-greyScale-90 text-text-primary transition-colors"
              onClick={() => viewMode === "days" ? navigateMonth(1) : navigateYearRange(1)}
            >
              <Icon name="arrow-right-2" size={18} />
            </button>
          </div>

          {viewMode === "days" ? (
            <>
              {/* Day-of-week headers */}
              <div className="grid grid-cols-7 mb-2">
                {DAY_LABELS.map((d) => (
                  <div key={d} className="text-center text-[11px] text-tertiary font-bold uppercase tracking-wider">
                    {d}
                  </div>
                ))}
              </div>

              {/* Day cells */}
              <div className="grid grid-cols-7">
                {Array.from({ length: firstDayOffset }).map((_, i) => (
                  <div key={`gap-${i}`} />
                ))}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const dateStr = toDateStr(viewYear, viewMonth, day);

                  const isStart = !!rangeStart && dateStr === rangeStart;
                  const isEnd = !!rangeEnd && dateStr === rangeEnd;
                  const inRange = !!(
                    rangeStart && rangeEnd && !isSingleDay &&
                    dateStr > rangeStart && dateStr < rangeEnd
                  );
                  const isSelected = isStart || isEnd;
                  const showRightStrip = isStart && !!rangeEnd && !isSingleDay;
                  const showLeftStrip = isEnd && !isSingleDay;
                  const isToday = dateStr === todayStr;

                  return (
                    <div key={day} className="relative flex items-center justify-center h-8">
                      {/* Continuous highlight strip */}
                      {inRange && <div className="absolute inset-0 bg-primary-50" />}
                      {showRightStrip && <div className="absolute inset-y-0 left-1/2 right-0 bg-primary-50" />}
                      {showLeftStrip && <div className="absolute inset-y-0 right-1/2 left-0 bg-primary-50" />}

                      <button
                        type="button"
                        className={[
                          "relative z-10 w-7 h-7 flex items-center justify-center rounded-full text-sm transition-all duration-150",
                          isSelected ? "bg-primary-600 text-white font-semibold shadow-md shadow-primary-600/20" : "",
                          !isSelected && inRange ? "text-text-primary" : "",
                          !isSelected && !inRange ? "hover:bg-greyScale-90 text-text-primary" : "",
                          isToday && !isSelected ? "font-bold text-primary-600" : "",
                        ].filter(Boolean).join(" ")}
                        onClick={() => handleDayClick(dateStr)}
                        onMouseEnter={() => {
                          if (fromValue && !toValue) setHoverDate(dateStr);
                        }}
                        onMouseLeave={() => setHoverDate(null)}
                      >
                        {day}
                      </button>
                    </div>
                  );
                })}
              </div>

              {fromValue && !toValue && (
                <p className="mt-3 text-center text-xs text-tertiary">Click a date to set end</p>
              )}
            </>
          ) : (
            /* Year grid */
            <div className="grid grid-cols-3 gap-2 py-2">
              {years.map((year) => {
                const isSelectedYear = year === viewYear;
                const isCurrentYear = year === today.getFullYear();
                return (
                  <button
                    key={year}
                    type="button"
                    className={[
                      "py-2.5 rounded-xl text-sm font-medium transition-all duration-200",
                      isSelectedYear
                        ? "bg-primary-600 text-white shadow-md shadow-primary-600/20"
                        : "hover:bg-primary-50 hover:text-primary-600 text-text-primary",
                      isCurrentYear && !isSelectedYear
                        ? "border border-primary-200 text-primary-600"
                        : "",
                    ].join(" ")}
                    onClick={() => {
                      setViewYear(year);
                      setViewMode("days");
                    }}
                  >
                    {year}
                  </button>
                );
              })}
            </div>
          )}
          </div>,
          document.body,
        )}

      {reserveHelperSpace || helperMessage ? (
        <Typography
          className="text-xs"
          tone={helperTone}
          data-testid={helperMessage ? `error-${dataTestId}` : undefined}
        >
          {helperMessage ?? "\u00A0"}
        </Typography>
      ) : null}
    </div>
  );
};

export default DateRangePicker;
